import { Alert, AlertIcon, Box, Card, CardBody, Heading, Icon, Tab, TabList, TabPanel, TabPanels, Tabs } from "@chakra-ui/react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { IconType } from "react-icons";
import {
  FaBell,
  FaBuilding,
  FaChartLine,
  FaFileContract,
  FaFileImport,
  FaFileSignature,
  FaListUl,
} from "react-icons/fa";
import { useSub } from "../../common/subscription/useSub";
import { SettingsTab, useQueryParams } from "../../common/useQueryParams";
import { ContractTemplates } from "../contracts/settings/ContractTemplates/ContractTemplates";
import { CustomContractFields } from "../contracts/settings/CustomContractFields/CustomContractFields";
import { AssessmentTemplates } from "./AssessmentTemplates";
import { DocumentTemplates } from "./DocumentTemplates";
import { InternalParties } from "./InternalParties";
import { NotificationSettings } from "./Notifications/NotificationSettings";
import { StandardTerms } from "./StandardTerms";

interface SettingsTabDefinition {
  key: SettingsTab;
  label: string;
  icon: IconType;
  isDisabled?: boolean;
  panel: ReactNode;
}

const PanelCard = ({ children }: { children: ReactNode }) => (
  <Card variant="outline" rounded="xl" bg="smBackground" borderColor="smBorder" boxShadow="sm">
    <CardBody overflowX="auto">{children}</CardBody>
  </Card>
);

export const SettingsPage = () => {
  const { t } = useTranslation();
  const sub = useSub();
  const params = useQueryParams();

  const tabs: SettingsTabDefinition[] = [
    { key: "notifications", label: t("Notification Settings"), icon: FaBell, panel: <NotificationSettings /> },
    {
      key: "document-templates",
      label: t("Document Templates"),
      icon: FaFileImport,
      isDisabled: !sub.hasDocumentTemplateAccess,
      panel: <DocumentTemplates />,
    },
    { key: "internal-parties", label: t("Internal Parties"), icon: FaBuilding, panel: <InternalParties /> },
    { key: "assessment-templates", label: t("Assessment Templates"), icon: FaChartLine, panel: <AssessmentTemplates /> },
    { key: "standard-terms", label: t("Standard terms"), icon: FaFileContract, panel: <StandardTerms /> },
    ...(sub.hasContractAccess && sub.canCreateContractTemplates
      ? [
          {
            key: "contract-types" as const,
            label: t("Contract Types"),
            icon: FaFileSignature,
            panel: <ContractTemplates />,
          },
        ]
      : []),
    ...(sub.hasContractAccess && sub.canConfigureContracts
      ? [
          {
            key: "custom-contract-fields" as const,
            label: t("Custom Contract Fields"),
            icon: FaListUl,
            panel: <CustomContractFields />,
          },
        ]
      : []),
  ];

  const selectedTab = params.getSettingsTab();
  const tabIndex = Math.max(
    0,
    tabs.findIndex((tab) => tab.key === selectedTab)
  );

  return (
    <Box>
      <Heading as="h1" size="lg" color="smText" mb="6">
        {t("Settings")}
      </Heading>
      {!sub.canUpdateOrganization && (
        <Alert status="warning" rounded="md" mb="5">
          <AlertIcon />
          {t("You can view the settings, but only organization administrators can change them.")}
        </Alert>
      )}
      <Tabs
        colorScheme="teal"
        index={tabIndex}
        onChange={(index) => params.setSelectedSettingsTab(tabs[index].key)}
        isLazy
      >
        <TabList
          overflowX="auto"
          overflowY="hidden"
          sx={{
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
            "& > button": { flexShrink: 0, whiteSpace: "nowrap" },
          }}
        >
          {tabs.map((tab) => (
            <Tab key={tab.key} isDisabled={tab.isDisabled}>
              <Icon as={tab.icon} mr="2" />
              {tab.label}
            </Tab>
          ))}
        </TabList>
        <TabPanels>
          {tabs.map((tab) => (
            <TabPanel key={tab.key} px="0" pt="5">
              <PanelCard>{tab.panel}</PanelCard>
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </Box>
  );
};
